/**
 * ============================================================
 * HOUSE OF KANZAN — Quiz Engine JavaScript
 * File:    assets/kanzan-quiz-engine.js
 * Section: kanzan-quiz-engine
 *
 * Behaviour:
 *   - Selecting an answer immediately advances to next question.
 *     No Next / Back buttons.
 *   - Progress bar fills with each answer.
 *   - Brief 320ms pause after selection so user sees their choice.
 *   - Loading screen shown between last answer and result.
 *   - Result shows personality + big tagline + description + products.
 *   - Share (WhatsApp + Copy link) and restart at the end.
 *
 * Data source: window.KQE_DATA injected by Liquid section.
 * Fallback questions used if no Question blocks are added yet.
 *
 * Maintained by: House of Kanzan
 * Last updated:  2026
 * ============================================================
 */

(function () {
  'use strict';


  // ── PERSONALITY EVALUATION ORDER ──────────────────────────────
  // Tiebreak winner is whichever appears first in this array.

  var PERSONALITY_ORDER = [
    'quiet_reader',
    'wanderer',
    'romantic',
    'night_walker'
  ];

  // Fallback personality data (no products) used before mapping block is added
  var FALLBACK_PERSONALITIES = {
    quiet_reader: {
      key: 'quiet_reader',
      display_name: 'Quiet Reader',
      tagline: 'Still. Private. Home.',
      description: 'You are drawn to quiet rooms, old pages, soft rituals, and fragrances that feel like private worlds.',
      products: []
    },
    wanderer: {
      key: 'wanderer',
      display_name: 'Wanderer',
      tagline: 'Alive. Easy. Moving.',
      description: 'You like movement, cafés, unfamiliar streets, and fragrances that feel easy to live with.',
      products: []
    },
    romantic: {
      key: 'romantic',
      display_name: 'Romantic',
      tagline: 'Warm. Soft. <em>Remembered.</em>',
      description: 'You notice feelings before details. Your fragrances should feel warm, soft, intimate, and a little remembered.',
      products: []
    },
    night_walker: {
      key: 'night_walker',
      display_name: 'Night Walker',
      tagline: 'Deep. Strange. <em>Beautiful.</em>',
      description: 'You are drawn to deeper rooms, late evenings, silence, incense, leather, woods, and strange beautiful moods.',
      products: []
    }
  };


  // ── STATE ──────────────────────────────────────────────────────

  var state = {
    currentQuestion: 0,
    scores: {},
    winnerKey: null,
    questions: [],
    personalities: {},
    settings: {}
  };


  // ── DATA RESOLUTION ───────────────────────────────────────────

  function resolveData() {
    var data = window.KQE_DATA || {};

    state.questions     = data.questions     || [];
    state.personalities = data.personalities || FALLBACK_PERSONALITIES;
    state.settings      = Object.assign({
      result_heading:   'Fragrances for you',
      show_vendor:      false,
      enable_quick_add: true,
      desktop_cols:     '3',
      mobile_cols:      '2'
    }, data.settings || {});
  }


  // ── DOM HELPERS ───────────────────────────────────────────────

  function el(id) { return document.getElementById(id); }
  function setText(id, text) { var e = el(id); if (e) e.textContent = text; }
  function setHTML(id, html) { var e = el(id); if (e) e.innerHTML = html; }


  // ── SCREEN MANAGEMENT ─────────────────────────────────────────

  function showScreen(screenId) {
    document.querySelectorAll('[data-kqe-screen]').forEach(function (s) {
      s.classList.remove('kqe-active');
    });
    var target = el(screenId);
    if (!target) return;
    void target.offsetWidth; // reflow to retrigger animation
    target.classList.add('kqe-active');
  }


  // ── PROGRESS BAR ──────────────────────────────────────────────

  function setProgress(answered, total) {
    var fill = el('kqe-progress-fill');
    if (!fill) return;
    fill.style.width = (total > 0 ? Math.round((answered / total) * 100) : 0) + '%';
  }


  // ── SCORING ───────────────────────────────────────────────────

  function initScores() {
    state.scores = {};
    PERSONALITY_ORDER.forEach(function (k) { state.scores[k] = 0; });
  }

  function addScore(key) {
    if (state.scores.hasOwnProperty(key)) state.scores[key]++;
  }


  // ── WINNER ────────────────────────────────────────────────────

  function calculateWinner() {
    var best = -1;
    var winner = PERSONALITY_ORDER[0];
    PERSONALITY_ORDER.forEach(function (k) {
      if ((state.scores[k] || 0) > best) {
        best = state.scores[k];
        winner = k;
      }
    });
    return winner;
  }


  // ── QUESTION RENDERING ────────────────────────────────────────

  function renderQuestion() {
    var questions = state.questions;
    var total = questions.length;
    var index = state.currentQuestion;

    if (index >= total) {
      // All questions answered — brief loading pause then result
      showScreen('kqe-loading-screen');
      setTimeout(function () {
        state.winnerKey = calculateWinner();
        renderResult();
      }, 1800);
      return;
    }

    var q = questions[index];

    setText('kqe-q-label', 'Question ' + (index + 1) + ' of ' + total);
    setText('kqe-q-text',  q.text);

    var container = el('kqe-options');
    if (!container) return;
    container.innerHTML = '';

    var labels  = ['A', 'B', 'C', 'D'];
    var options = [
      { text: q.option_a_text, key: q.option_a_personality },
      { text: q.option_b_text, key: q.option_b_personality },
      { text: q.option_c_text, key: q.option_c_personality },
      { text: q.option_d_text, key: q.option_d_personality }
    ];

    options.forEach(function (opt, i) {
      if (!opt.text) return;

      var btn = document.createElement('button');
      btn.className = 'kqe-option';
      btn.type = 'button';
      btn.innerHTML =
        '<span class="kqe-opt-label">' + labels[i] + '</span>' +
        '<span class="kqe-opt-main">' + opt.text + '</span>';

      // Closure so each button keeps its own key
      (function (button, personalityKey) {
        button.addEventListener('click', function () {
          // Mark selected
          container.querySelectorAll('.kqe-option').forEach(function (b) {
            b.classList.remove('kqe-selected');
            b.disabled = true;
          });
          button.classList.add('kqe-selected');

          // Score and advance
          addScore(personalityKey);
          state.currentQuestion++;
          setProgress(state.currentQuestion, total);

          setTimeout(function () { renderQuestion(); }, 320);
        });
      })(btn, opt.key);

      container.appendChild(btn);
    });

    showScreen('kqe-question-screen');
  }


  // ── RESULT RENDERING ──────────────────────────────────────────

  function renderResult() {
    var winner = state.personalities[state.winnerKey];
    if (!winner) return;

    // Personality name (spaced uppercase, dust color)
    setText('kqe-archetype', winner.display_name);

    // Big 2-3 word tagline — may contain <em> for italic red
    setHTML('kqe-result-h2', winner.tagline || winner.display_name);

    // Long description paragraph
    setText('kqe-result-story', winner.description);

    // Section label from settings
    setText('kqe-scents-label', state.settings.result_heading || 'Fragrances for you');

    // Product grid cards
    renderProductGrid(winner.products || []);

    setProgress(1, 1);
    showScreen('kqe-result-screen');
  }


  // ── PRODUCT GRID ──────────────────────────────────────────────
  // Grid card layout. Container is #kqe-product-grid.
  // Each card: image / title / price / view link / optional quick add.

  function renderProductGrid(products) {
    var grid = el('kqe-product-grid');
    if (!grid) return;
    grid.innerHTML = '';

    if (!products || !products.length) {
      var empty = document.createElement('p');
      empty.style.cssText = 'font-size:1.3rem;color:var(--kz-ghost);font-style:italic;grid-column:1/-1;';
      empty.textContent = 'Add products to this personality in the Theme Editor.';
      grid.appendChild(empty);
      return;
    }

    products.forEach(function (p) {
      var card = buildProductCard(p);
      grid.appendChild(card);
    });
  }

  function buildProductCard(p) {
    var card = document.createElement('article');
    card.className = 'kqe-product-card';

    // ── Image
    var imageWrap = document.createElement('div');
    imageWrap.className = 'kqe-product-card__image-wrap';

    if (p.image_url) {
      var img = document.createElement('img');
      img.className = 'kqe-product-card__image';
      img.src = p.image_url;
      img.alt = p.image_alt || p.title || '';
      img.loading = 'lazy';
      img.width = 400;
      img.height = 400;
      imageWrap.appendChild(img);
    } else {
      var ph = document.createElement('div');
      ph.className = 'kqe-product-card__no-image';
      ph.textContent = '香';
      imageWrap.appendChild(ph);
    }
    card.appendChild(imageWrap);

    // ── Body
    var body = document.createElement('div');
    body.className = 'kqe-product-card__body';

    if (state.settings.show_vendor && p.vendor) {
      var vendor = document.createElement('span');
      vendor.className = 'kqe-product-card__vendor';
      vendor.textContent = p.vendor;
      body.appendChild(vendor);
    }

    var title = document.createElement('div');
    title.className = 'kqe-product-card__title';
    title.textContent = p.title || '';
    body.appendChild(title);

    if (p.description) {
      var desc = document.createElement('p');
      desc.className = 'kqe-product-card__desc';
      desc.textContent = p.description;
      body.appendChild(desc);
    }

    var priceWrap = document.createElement('div');
    priceWrap.className = 'kqe-product-card__price';
    priceWrap.textContent = p.price || '';
    if (p.compare_at) {
      var compare = document.createElement('span');
      compare.className = 'kqe-product-card__compare';
      compare.textContent = p.compare_at;
      priceWrap.appendChild(compare);
    }
    body.appendChild(priceWrap);
    card.appendChild(body);

    // ── Actions
    var actions = document.createElement('div');
    actions.className = 'kqe-product-card__actions';

    // Quick Add — native Shopify cart form, no AJAX
    if (state.settings.enable_quick_add && p.variant_id && p.available) {
      var form = document.createElement('form');
      form.method = 'post';
      form.action = '/cart/add';

      var varInput = document.createElement('input');
      varInput.type = 'hidden';
      varInput.name = 'id';
      varInput.value = p.variant_id;

      var qtyInput = document.createElement('input');
      qtyInput.type = 'hidden';
      qtyInput.name = 'quantity';
      qtyInput.value = '1';

      var addBtn = document.createElement('button');
      addBtn.type = 'submit';
      addBtn.className = 'kqe-product-card__add';
      addBtn.textContent = 'Add to Cart';

      form.appendChild(varInput);
      form.appendChild(qtyInput);
      form.appendChild(addBtn);
      actions.appendChild(form);
    }

    // View product link
    var viewLink = document.createElement('a');
    viewLink.href = p.url || '#';
    viewLink.className = 'kqe-product-card__view';
    viewLink.textContent = 'View Fragrance';
    actions.appendChild(viewLink);

    card.appendChild(actions);
    return card;
  }


  // ── SHARE ─────────────────────────────────────────────────────

  function shareWhatsApp() {
    var personality = state.personalities[state.winnerKey];
    var name = personality ? personality.display_name : '';
    var text = encodeURIComponent(
      'I took the House of Kanzan scent quiz. My result: ' + name + '.\n\nFind yours: ' + window.location.href
    );
    window.open('https://wa.me/?text=' + text, '_blank');
  }

  function copyLink() {
    var btn = el('kqe-share-copy');
    var url = window.location.href;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(function () {
        if (btn) { btn.textContent = 'Copied'; setTimeout(function () { btn.textContent = 'Copy link'; }, 2000); }
      });
    } else {
      var ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      if (btn) { btn.textContent = 'Copied'; setTimeout(function () { btn.textContent = 'Copy link'; }, 2000); }
    }
  }


  // ── RESTART ───────────────────────────────────────────────────

  function restart() {
    state.currentQuestion = 0;
    state.winnerKey = null;
    initScores();
    setProgress(0, 1);
    showScreen('kqe-intro-screen');
  }


  // ── INIT ──────────────────────────────────────────────────────

  function init() {
    resolveData();
    initScores();

    var beginBtn   = el('kqe-begin');
    var restartBtn = el('kqe-restart');
    var waBtn      = el('kqe-share-wa');
    var copyBtn    = el('kqe-share-copy');

    if (beginBtn)   beginBtn.addEventListener('click',   function () { renderQuestion(); });
    if (restartBtn) restartBtn.addEventListener('click',  restart);
    if (waBtn)      waBtn.addEventListener('click',       shareWhatsApp);
    if (copyBtn)    copyBtn.addEventListener('click',     copyLink);

    showScreen('kqe-intro-screen');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();